import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import Button from "@/components/Button/Button";
import { primaryNav, site } from "@/content/site";
import styles from "./Header.module.css";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Render the mobile menu panel via a portal to document.body so its
  // `position: fixed` is anchored to the viewport. The <header> uses
  // `backdrop-filter`, which (per spec) creates a containing block for
  // fixed-positioned descendants — that was collapsing the panel to 0px
  // and making the menu appear broken.
  const mobilePanel =
    typeof document !== "undefined"
      ? createPortal(
          <div
            id="mobile-menu"
            className={[styles.mobilePanel, open ? styles.mobilePanelOpen : ""].join(" ")}
            aria-hidden={!open}
          >
            <nav className={styles.mobileNav} aria-label="Mobile">
              {primaryNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    [styles.mobileLink, isActive ? styles.mobileLinkActive : ""].join(" ")
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className={styles.mobileCtas}>
                <Button as="link" to="/contact" variant="primary" size="lg">
                  Schedule a consultation
                </Button>
                <Button as="a" href={site.links.headway} variant="secondary" size="lg">
                  Verify insurance via Headway
                </Button>
              </div>
            </nav>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <header className={[styles.header, scrolled ? styles.scrolled : ""].join(" ")}>
        <div className={styles.inner}>
          <Link to="/" className={styles.brand} aria-label="Home">
            <span className={styles.brandName}>{site.practitioner.name}</span>
            <span className={styles.brandCreds}>{site.practitioner.credentials}</span>
          </Link>

          <nav className={styles.desktopNav} aria-label="Primary">
            {primaryNav.slice(0, -1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [styles.navLink, isActive ? styles.navLinkActive : ""].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className={styles.actions}>
            <div className={styles.desktopCta}>
              <Button as="link" to="/contact" variant="primary" size="md">
                Get in touch
              </Button>
            </div>
            <button
              type="button"
              className={styles.menuToggle}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => setOpen((v) => !v)}
            >
              <span className={styles.menuBars}>
                <span className={[styles.menuBar, open ? styles.menuBarOpenTop : ""].join(" ")} />
                <span className={[styles.menuBar, open ? styles.menuBarOpenMid : ""].join(" ")} />
                <span className={[styles.menuBar, open ? styles.menuBarOpenBot : ""].join(" ")} />
              </span>
            </button>
          </div>
        </div>
      </header>
      {mobilePanel}
    </>
  );
}
